import React, {useEffect, useId, useRef, useState} from 'react'; // Added useId
import {Box, Button, Dialog, DialogContent, Grid, LinearProgress, Typography} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import ShareIcon from '@mui/icons-material/Share';
import VideocamIcon from '@mui/icons-material/Videocam';
import ImageIcon from '@mui/icons-material/Image';
import {styled} from '@mui/material/styles';
import {useImageProcessor} from './FormControl/hooks/useImageProcessor';
import {GenerationActionSource} from '../../services/jobService';
import {useTemplate} from '../../context/TemplateContext';
import {designLabel} from '../../models/Template';
import {useTranslation} from 'react-i18next';
import {useMobile} from '../../context/MobileContext';
import {DesignGridContainer, DesignOption, DesignOptionWrapper} from './components/DesignButtons';
import {ShimmerContainer, ShimmerEffect} from "../../utils/shimmer";
import {CloseButton, CloseButtonContainer} from "./components/CloseButton";
import {useStatsLogger} from '../../context/ApiServiceContext';
import {StatEventType} from "../../models/StatEvent"; // Import the stats logger hook
import {toast} from 'react-toastify';

// Styled components (keep existing)
const StyledDialog = styled(Dialog)(({theme}) => ({
    '& .MuiDialog-paper': {
        background: "linear-gradient(220deg, #051726 36%, #025989 100%)",
        borderRadius: '16px',
        maxWidth: '560px',
        width: '100%',
        margin: theme.spacing(2),
        boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.5)',
    }
}));

const HeaderText = styled(Typography)(({theme}) => ({
    color: 'white',
    fontWeight: 600,
    fontSize: '24px',
    marginBottom: theme.spacing(3),
}));

const ActionButton = styled(Button)(({theme}) => ({
    borderRadius: '24px',
    padding: theme.spacing(1, 3),
    textTransform: 'none',
    fontSize: '16px',
    fontWeight: 500,
    minWidth: '140px',
}));

const DownloadButton = styled(ActionButton)(({theme}) => ({
    backgroundColor: '#00E162',
    color: 'white',
    '&:hover': {
        backgroundColor: '#43A047',
    },
}));

const ShareButton = styled(ActionButton)(({theme}) => ({
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    '&:hover': {
        backgroundColor: '#3367D6',
    },
}));

const ImageContainer = styled(Grid)(({theme}) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    '& img': {
        maxWidth: '100%',
        maxHeight: '450px',
        objectFit: 'contain',
    },
}));


interface ShareModalProps {
    open: boolean;
    onClose: () => void;
    designIndex: number;
    // Add generationId prop to link events if available from parent
    generationId?: string;
}

const ShareModal: React.FC<ShareModalProps> = ({open, onClose, designIndex, generationId: initialGenerationId}) => {
    const {t} = useTranslation();
    const {isMobile} = useMobile();
    const [selectedDesignIndex, setSelectedDesignIndex] = useState<number>(designIndex);
    const {template} = useTemplate();
    const {isLoading, processImage, hasVideoBackground, lastBlob, setLastBlob, lastVideoUrl, lastMediaType, progress, progressMessage} = useImageProcessor(designIndex);
    const [shimmerDimensions, setShimmerDimensions] = useState({width: 300, height: 450});
    const [isFetchingMedia, setIsFetchingMedia] = useState(false);
    // When a video is selected the user must first pick an output: a composited
    // video ("Create video") or a still PNG ("Download image"). We only auto-
    // generate when there's no video (image is the only sensible output).
    const [awaitingFormatChoice, setAwaitingFormatChoice] = useState(false);
    const statsLogger = useStatsLogger(); // Get stats logger instance
    const modalGenerationId = useId(); // Fallback id, used until the server hands us a jobId

    // Use the passed generationId if available, otherwise use the one generated for this modal
    const fallbackGenerationId = initialGenerationId || modalGenerationId;

    // The server jobId IS the generationId of the durable generation record, so
    // it is the correlation id every stats event for this generation carries.
    // It arrives asynchronously (as soon as the job is created) while the
    // success/error loggers below were already handed to useImageProcessor —
    // hence a ref, not state: a state value would be stale inside them.
    const generationIdRef = useRef<string>(fallbackGenerationId);

    // Videos stream straight from the presigned S3 URL; images come back as a blob
    const hasMedia = lastMediaType === 'video' ? !!lastVideoUrl : !!lastBlob;
    const mediaUrl = lastMediaType === 'video'
        ? (lastVideoUrl ?? '')
        : (lastBlob ? URL.createObjectURL(lastBlob) : '');

    // Download/share need the actual bytes — fetch them only when the user
    // asks (and cache in lastBlob), so the preview never depends on a
    // 20-45MB download succeeding.
    const ensureMediaBlob = async (): Promise<Blob | null> => {
        if (lastBlob) return lastBlob;
        if (!lastVideoUrl) return null;
        setIsFetchingMedia(true);
        try {
            // no-store: never reuse the HTTP cache entry the <video> preview
            // created — if that entry was fetched without CORS it has no
            // Access-Control-Allow-Origin and this CORS fetch would be blocked.
            // (A cache-busting param is not an option: the URL is presigned and
            // extra params invalidate the signature.)
            const response = await fetch(lastVideoUrl, { cache: 'no-store' });
            if (!response.ok) {
                throw new Error(`Failed to fetch video: ${response.status}`);
            }
            const blob = await response.blob();
            setLastBlob(blob);
            return blob;
        } catch (error) {
            console.error('Error fetching media for download/share:', error);
            toast.error(t('error.imageProcessing') || 'Failed to fetch media.');
            return null;
        } finally {
            setIsFetchingMedia(false);
        }
    };

    // Set shimmer dimensions based on the template design
    useEffect(() => {
        if (template?.designs && template.designs[selectedDesignIndex]) {
            const design = template.designs[selectedDesignIndex];
            const maxWidth = 500;
            const maxHeight = 450;
            let width = design.width || 300;
            let height = design.height || 450;

            if (width > maxWidth) {
                const ratio = maxWidth / width;
                width = maxWidth;
                height = height * ratio;
            }
            if (height > maxHeight) {
                const ratio = maxHeight / height;
                height = maxHeight;
                width = width * ratio;
            }
            setShimmerDimensions({width, height});
        }
    }, [template, selectedDesignIndex]);

    // Kick off a generation for a specific output format. The start event is
    // logged from onJobCreated rather than here, so it carries the server
    // jobId — the same correlation id the success/download/share events use.
    const beginGeneration = (designIdx: number, format: 'image' | 'video', actionSource: GenerationActionSource) => {
        setAwaitingFormatChoice(false);
        // Until the job exists, fall back to the client-side id so a create
        // failure isn't attributed to the previous generation.
        generationIdRef.current = fallbackGenerationId;
        processImage(designIdx, format, logImageGenerationSuccess, logImageGenerationError, {
            actionSource,
            onJobCreated: (jobId) => {
                generationIdRef.current = jobId;
                if (template) {
                    statsLogger.logGenerationStart(
                        template.id,
                        template.name,
                        template.designs[designIdx]?.key ?? String(designIdx),
                        designLabel(template.designs[designIdx]) || 'Unknown Design',
                        jobId
                    );
                }
            },
        });
    };

    // Decide how to start for a design: videos need an explicit output choice
    // (video vs still image); everything else auto-generates a still image.
    const prepareForDesign = (designIdx: number, actionSource: GenerationActionSource) => {
        setLastBlob(null);
        if (hasVideoBackground(designIdx)) {
            setAwaitingFormatChoice(true);
        } else {
            beginGeneration(designIdx, 'image', actionSource);
        }
    };

    const handleTypeSelect = (selectedDesign: number) => {
        setSelectedDesignIndex(selectedDesign);
        // User picked this design — an explicit generate, not the on-open preview.
        prepareForDesign(selectedDesign, 'explicit');
    };

    const handleDownload = async () => {
        if (!template) return;
        const blob = await ensureMediaBlob();
        if (blob) {
            const design = template.designs[selectedDesignIndex];
            const designName = designLabel(design) || 'design';
            const designKey = design?.key ?? String(selectedDesignIndex);

            // Log download event
            statsLogger.logDownload(template.id, template.name, designKey, designName, generationIdRef.current);

            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            // NEW: Use correct file extension based on media type
            const extension = lastMediaType === 'video' ? 'mp4' : 'png';
            a.download = `${template?.name || 'template'}-${designName}.${extension}`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        }
    };

    const handleShare = async () => {
        if (!template) return;
        const blob = await ensureMediaBlob();
        if (!blob) return;

        const design = template.designs[selectedDesignIndex];
        const designName = designLabel(design) || 'design';
        const designKey = design?.key ?? String(selectedDesignIndex);
        const name = `${template?.name} - ${designName}`;
        // NEW: Use correct extension and MIME type
        const extension = lastMediaType === 'video' ? 'mp4' : 'png';
        const mimeType = lastMediaType === 'video' ? 'video/mp4' : 'image/png';

        try {
            if (navigator.share) {
                const file = new File([blob], `${name}.${extension}`, {type: mimeType});
                await navigator.share({
                    title: name,
                    files: [file]
                });
                // Log share event *after* successful share API call (if possible)
                // If navigator.share doesn't provide success feedback, log immediately
                statsLogger.logShare(template.id, template.name, designKey, designName, generationIdRef.current);
            }
        } catch (error) {
            // Don't log share if the user cancels the share dialog
            if (error instanceof DOMException && error.name === 'AbortError') {
                console.log('Share cancelled by user.');
            } else {
                console.error('Error sharing:', error);
                // Optionally log a share *attempt* failure?
            }
        }
    };

    // Generate image on modal open and log start event
    useEffect(() => {
        if (open) {
            setSelectedDesignIndex(designIndex);
            // For videos this shows the format-choice buttons; otherwise it
            // auto-generates a still image (and logs the generation-start event).
            // Nobody asked for this render — record it as an auto-preview so it
            // doesn't inflate the explicit-generate counts.
            prepareForDesign(designIndex, 'auto-preview');
        }
    }, [open, designIndex, template]); // Added template dependency

    // Log successful generation when lastBlob appears (simplistic approach)
    // A better approach would be if useImageProcessor provided success/error status
    const logImageGenerationSuccess = (durationMs: number) => {
        if (template) {
            const design = template.designs[selectedDesignIndex];
            statsLogger.logEvent({
                eventType: StatEventType.IMAGE_GENERATED,
                templateId: template.id,
                templateName: template.name,
                designKey: design?.key ?? String(selectedDesignIndex),
                designName: designLabel(design) || 'Unknown Design',
                generationId: generationIdRef.current,
                durationMs
                // Add duration if available
            });
        }
    }

    const logImageGenerationError = (errorMessage: string) => {
        if (template) {
            const design = template.designs[selectedDesignIndex];
            statsLogger.logEvent({
                eventType: StatEventType.IMAGE_GENERATION_FAILED,
                templateId: template.id,
                templateName: template.name,
                designKey: design?.key ?? String(selectedDesignIndex),
                designName: designLabel(design) || 'Unknown Design',
                generationId: generationIdRef.current,
                errorMessage
                // Add duration if available
            });
        }
    }


    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
        >
            {/* The whole row closes, not just the 30px icon — on touch everyone
                taps the WORD "סגור", which used to be an inert Typography. */}
            <CloseButtonContainer onClick={onClose} sx={{cursor: 'pointer', zIndex: 1, padding: '6px'}}>
                <Typography marginRight={1}>{t("editor.generateModal.close")}</Typography>
                <CloseButton>
                    <CloseIcon sx={{fontSize: '20px'}}/>
                </CloseButton>
            </CloseButtonContainer>

            <DialogContent>
                <Grid container sx={{p: 1, paddingTop: 3}} justifyContent={'center'}>
                    <Grid item
                          sx={{
                              width: '100%',
                              marginTop: 3,
                              display: 'flex',
                              justifyContent: 'space-between'
                          }}>
                        <HeaderText>{t('editor.generateModal.title')}</HeaderText>
                        <DesignGridContainer
                            container
                            direction="row"
                            sx={{
                                width: "unset",
                                position: "relative",
                                top: 2
                            }}
                        >
                            <DesignOptionWrapper
                                sx={{
                                    justifyContent: isMobile ? "center" : "unset",
                                }}>
                                {template?.designs.map((design, index) => (
                                    <DesignOption
                                        key={`design-${index}`}
                                        variant="h6"
                                        isActive={selectedDesignIndex === index}
                                        onClick={() => handleTypeSelect(index)}
                                    >
                                        {designLabel(design)}
                                    </DesignOption>
                                ))}
                            </DesignOptionWrapper>
                        </DesignGridContainer>
                    </Grid>

                    <ImageContainer item>
                        {isLoading ? (
                            <ShimmerContainer>
                                <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
                                    <ShimmerEffect sx={{
                                        width: `${shimmerDimensions.width}px`,
                                        height: `${shimmerDimensions.height}px`
                                    }}>
                                        <Typography color="white">{t("editor.buttons.preparingFile")}</Typography>
                                    </ShimmerEffect>
                                    {/* Progress bar for video generation */}
                                    <Box sx={{ width: shimmerDimensions.width, mt: 2 }}>
                                        <Typography
                                            color="white"
                                            sx={{ fontSize: '14px', mb: 0.5, textAlign: 'center' }}
                                        >
                                            {progressMessage || `${progress}%`}
                                        </Typography>
                                        <LinearProgress
                                            variant="determinate"
                                            value={progress}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                backgroundColor: 'rgba(255,255,255,0.2)',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 4,
                                                    backgroundColor: '#00E162',
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </ShimmerContainer>
                        ) : awaitingFormatChoice ? (
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    width: shimmerDimensions.width,
                                    minHeight: shimmerDimensions.height,
                                }}
                            >
                                <Typography color="white" sx={{mb: 1, textAlign: 'center'}}>
                                    {t('editor.share.chooseOutput')}
                                </Typography>
                                <ShareButton
                                    startIcon={<VideocamIcon/>}
                                    onClick={() => beginGeneration(selectedDesignIndex, 'video', 'explicit')}
                                >
                                    {t('editor.share.createVideo')}
                                </ShareButton>
                                <DownloadButton
                                    startIcon={<ImageIcon/>}
                                    onClick={() => beginGeneration(selectedDesignIndex, 'image', 'explicit')}
                                >
                                    {t('editor.share.downloadImage')}
                                </DownloadButton>
                            </Box>
                        ) : hasMedia ? (
                            lastMediaType === 'video' ? (
                                <video
                                    src={mediaUrl}
                                    // anonymous CORS mode so Chrome's HTTP cache stores the S3
                                    // response WITH Access-Control-Allow-Origin — without it the
                                    // later download/share fetch() of the same URL is served the
                                    // cached CORS-less response and is blocked ("Failed to fetch").
                                    crossOrigin="anonymous"
                                    controls
                                    autoPlay
                                    loop
                                    muted
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '450px',
                                        objectFit: 'contain',
                                    }}
                                />
                            ) : (
                                <img src={mediaUrl} alt="Generated design"/>
                            )
                        ) : (
                            <Typography color="text.secondary">{t('editor.generateModal.error')}</Typography> // This might show briefly before loading or on actual error
                        )}
                    </ImageContainer>

                    <Grid item display="flex" width={'100%'} gap={1} justifyContent="center">
                        <DownloadButton
                            startIcon={<DownloadIcon/>}
                            onClick={handleDownload}
                            disabled={isLoading || isFetchingMedia || !hasMedia}
                        >
                            {t("editor.buttons.download")}
                        </DownloadButton>
                        {'share' in navigator && <ShareButton
                            startIcon={<ShareIcon/>}
                            onClick={handleShare}
                            disabled={isLoading || isFetchingMedia || !hasMedia}
                        >
                            {t("editor.buttons.share")}
                        </ShareButton>}
                    </Grid>
                </Grid>
            </DialogContent>
        </StyledDialog>
    );
};

export default ShareModal;
